import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getExportDefinitionOptions } from "../../api/exports/getExportDefinitionOptions";
import { updateExportDefinition } from "../../api/exports/updateExportDefinition";
import { useTranslationHook } from "../../hooks";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { ExportDefinition } from "../../types/model/export/ExportDefinition";
import { ExportDefinitionOptions } from "../../types/model/export/ExportDefinitionOptions";
import { UpdateExportDefinitionRequest } from "../../types/model/export/UpdateExportDefinitionRequest";
import JoinsTree from "./JoinsTree";

interface UpdateExportDefinitionDialogProps {
  open: boolean;
  exportDefinition: ExportDefinition | null;
  onClose: () => void;
  onSuccess: () => void;
}

type JoinOption = ExportDefinitionOptions["joins"][number];
type JoinPathHop = JoinOption["path"][number];

interface SelectedColumn {
  column: string;
  label: string;
  table?: string;
  joinKey?: string;
  path?: JoinPathHop[];
}

const serializePath = (path: JoinPathHop[]) =>
  path.map((h) => `${h.fromColumn}>${h.toTable}.${h.toColumn}`).join("|");

const columnKey = (column: string, joinKey?: string) => `${joinKey ?? "__base__"}.${column}`;

const UpdateExportDefinitionDialog = ({
  open,
  exportDefinition,
  onClose,
  onSuccess,
}: UpdateExportDefinitionDialogProps) => {
  const { t } = useTranslationHook();
  const handleError = useErrorHandler();

  const [name, setName] = useState("");
  const [options, setOptions] = useState<ExportDefinitionOptions | null>(null);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [selectedColumns, setSelectedColumns] = useState<SelectedColumn[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !exportDefinition) return;

    setName(exportDefinition.name);
    setErrorMessage(null);

    setSelectedColumns(
      exportDefinition.columns.map((c) => {
        const matchingJoin = exportDefinition.joins.find((j) => j.table === c.table);

        return {
          column: c.column,
          label: c.label,
          table: c.table,
          joinKey: matchingJoin ? serializePath(matchingJoin.path) : undefined,
          path: matchingJoin?.path,
        };
      }),
    );

    const fetchOptions = async () => {
      setLoadingOptions(true);
      try {
        const data = await getExportDefinitionOptions(exportDefinition.id);
        setOptions(data);
      } catch (error) {
        handleError(error);
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchOptions();
  }, [open, exportDefinition]);

  const isColumnSelected = (column: string, joinKey?: string) =>
    selectedColumns.some((c) => columnKey(c.column, c.joinKey) === columnKey(column, joinKey));

  const toggleColumn = useCallback((column: string, join?: JoinOption) => {
    const joinKey = join ? serializePath(join.path) : undefined;

    setSelectedColumns((prev) => {
      const exists = prev.some((c) => columnKey(c.column, c.joinKey) === columnKey(column, joinKey));

      if (exists) {
        return prev.filter((c) => columnKey(c.column, c.joinKey) !== columnKey(column, joinKey));
      }

      return [
        ...prev,
        { column, label: column, table: join?.table, joinKey, path: join?.path },
      ];
    });
  }, []);

  const updateColumnLabel = useCallback((column: string, join: JoinOption | undefined, label: string) => {
    const joinKey = join ? serializePath(join.path) : undefined;

    setSelectedColumns((prev) =>
      prev.map((c) =>
        columnKey(c.column, c.joinKey) === columnKey(column, joinKey) ? { ...c, label } : c,
      ),
    );
  }, []);

  const selectedJoins = useMemo(() => {
    const map = new Map<string, JoinPathHop[]>();

    selectedColumns.forEach((c) => {
      if (c.joinKey && c.path && !map.has(c.joinKey)) {
        map.set(c.joinKey, c.path);
      }
    });

    return map;
  }, [selectedColumns]);

  const canSave = name.trim().length > 0 && selectedColumns.length > 0;

  const handleSubmit = async () => {
    if (!exportDefinition) return;

    if (!canSave) {
      setErrorMessage(t("exports.admin.dialog.select-at-least-one-column"));
      return;
    }

    const joins = Array.from(selectedJoins.entries()).map(([, path]) => {
      const lastHop = path[path.length - 1];
      return { table: lastHop.toTable, path };
    });

    const payload: UpdateExportDefinitionRequest = {
      name: name.trim(),
      columns: selectedColumns.map((c) => ({
        label: c.label,
        column: c.column,
        table: c.table,
      })),
      joins,
    };

    setSubmitting(true);
    setErrorMessage(null);
    try {
      await updateExportDefinition(exportDefinition.id, payload);
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      setErrorMessage(t("generic.errors.something-went-wrong"));
    } finally {
      setSubmitting(false);
    }
  };

  const baseColumnsCount = selectedColumns.filter((c) => !c.table).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack spacing={1}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <Typography variant="h6">{t("exports.admin.dialog.edit-export")}</Typography>

            {exportDefinition && (
              <Chip label={exportDefinition.name} size="small" color="secondary" sx={{ fontWeight: 600 }} />
            )}
          </Box>

          <Typography variant="body2" color="text.secondary">
            {t("exports.admin.dialog.description")}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mt: 1 }}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              required
              label={t("exports.admin.dialog.name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={submitting}
            />

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                {t("exports.admin.dialog.base-columns")}
                {baseColumnsCount > 0 && (
                  <Chip label={baseColumnsCount} size="small" sx={{ ml: 1, height: 20 }} />
                )}
              </Typography>

              <Stack spacing={1}>
                {(options?.columns ?? []).map((opt) => {
                  const selected = isColumnSelected(opt.column);
                  const current = selectedColumns.find(
                    (c) => columnKey(c.column, c.joinKey) === columnKey(opt.column),
                  );

                  return (
                    <Stack
                      key={opt.column}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ opacity: submitting ? 0.6 : 1 }}
                    >
                      <FormControlLabel
                        sx={{ flex: 1, minWidth: 0 }}
                        control={
                          <Checkbox
                            checked={selected}
                            disabled={submitting}
                            onChange={() => toggleColumn(opt.column)}
                          />
                        }
                        label={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2">{opt.column}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {opt.dataType}
                            </Typography>
                          </Stack>
                        }
                      />

                      {selected && (
                        <TextField
                          size="small"
                          label={t("exports.admin.dialog.column-label")}
                          value={current?.label ?? ""}
                          disabled={submitting}
                          onChange={(e) => updateColumnLabel(opt.column, undefined, e.target.value)}
                          sx={{ width: 300 }}
                        />
                      )}
                    </Stack>
                  );
                })}

                {!loadingOptions && (options?.columns ?? []).length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {t("generic.no-data")}
                  </Typography>
                )}
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                {t("exports.admin.dialog.available-joins")}
              </Typography>

              {!loadingOptions && (
                <JoinsTree
                  joins={options?.joins ?? []}
                  selectedColumns={selectedColumns}
                  onToggleColumn={toggleColumn}
                  onUpdateColumnLabel={updateColumnLabel}
                  submitting={submitting}
                />
              )}
            </Box>

            {errorMessage && (
              <Typography color="error" variant="body2">
                {errorMessage}
              </Typography>
            )}
          </Stack>
        </Paper>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t("generic.cancel")}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting || !canSave}>
          {t("generic.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateExportDefinitionDialog;